import {
  collection,
  query,
  where,
  orderBy,
  limit,
  getDocs,
  doc,
  getDoc,
  updateDoc,
  Timestamp,
  startAfter,
  increment,
  serverTimestamp,
  writeBatch
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { Article } from '../types/firebase';
import { User } from 'firebase/auth';
import { validateArticleUrl, extractArticleMetadata, ArticleMetadata } from '../utils/articleValidation';
import { addArticleReaction, removeArticleReaction } from './articleReactionService';

const articlesCollection = collection(db, 'articles');

export const createArticle = async (
  articleUrl: string,
  causes: string[],
  primaryCause: string,
  user: User,
  metadata?: ArticleMetadata
): Promise<string> => {
  try {
    // Validate the article URL
    const validation = validateArticleUrl(articleUrl);
    if (!validation.isValid) {
      throw new Error(validation.error);
    }

    // Extract article metadata if not provided
    const articleMetadata = metadata || await extractArticleMetadata(articleUrl);
    
    // Check for duplicate articles
    const duplicateQuery = query(
      articlesCollection,
      where('articleUrl', '==', articleUrl)
    );
    const duplicateSnapshot = await getDocs(duplicateQuery);
    
    if (!duplicateSnapshot.empty) {
      throw new Error('This article has already been shared.');
    }

    // Create the article document
    const articleData: Omit<Article, 'id'> = {
      articleUrl,
      title: articleMetadata.title,
      description: articleMetadata.description,
      thumbnail: articleMetadata.thumbnail,
      date: Timestamp.now(),
      causes,
      primaryCause,
      userId: user.uid,
      userName: user.displayName || 'Anonymous',
      userAvatar: user.photoURL || '',
      source: articleMetadata.source,
      ...(articleMetadata.author && { author: articleMetadata.author }),
      reactions: {
        thumbsUp: { count: 0, users: [] },
        thumbsDown: { count: 0, users: [] },
        love: { count: 0, users: [] },
        laugh: { count: 0, users: [] },
        wow: { count: 0, users: [] },
        cry: { count: 0, users: [] },
        vomit: { count: 0, users: [] },
        angry: { count: 0, users: [] },
        poop: { count: 0, users: [] },
        clown: { count: 0, users: [] }
      },
      commentCount: 0,
      lastUpdated: Timestamp.now(),
      type: 'article'
    };

    const batch = writeBatch(db);
    const articleRef = doc(articlesCollection);
    
    // Add the article
    batch.set(articleRef, articleData);

    // Update user's stats
    const userRef = doc(db, 'users', user.uid);
    batch.update(userRef, {
      lastActive: serverTimestamp()
    });

    await batch.commit();
    
    return articleRef.id;
  } catch (error) {
    console.error('Error creating article:', error);
    throw error;
  }
};

export const getArticles = async (
  filters: {
    cause?: string;
    source?: string;
  } = {},
  sort: {
    field: 'date' | 'commentCount';
    direction: 'asc' | 'desc';
  } = { field: 'date', direction: 'desc' },
  lastVisible?: any,
  pageSize: number = 10
): Promise<{ articles: Article[]; lastVisible: any }> => {
  try {
    let q = query(articlesCollection);

    // Apply filters
    if (filters.cause) {
      q = query(q, where('primaryCause', '==', filters.cause));
    }
    if (filters.source) {
      q = query(q, where('source', '==', filters.source));
    }

    // Apply sorting
    q = query(q, orderBy(sort.field, sort.direction));

    // Apply pagination
    if (lastVisible) {
      q = query(q, startAfter(lastVisible));
    }
    q = query(q, limit(pageSize));

    const snapshot = await getDocs(q);
    const articles = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    })) as Article[];

    return {
      articles,
      lastVisible: snapshot.docs[snapshot.docs.length - 1] || null
    };
  } catch (error) {
    console.error('Error fetching articles:', error);
    throw error;
  }
};

export const getArticle = async (articleId: string): Promise<Article | null> => {
  try {
    const articleRef = doc(articlesCollection, articleId);
    const articleDoc = await getDoc(articleRef);
    
    if (!articleDoc.exists()) {
      return null;
    }
    
    return { id: articleDoc.id, ...articleDoc.data() } as Article;
  } catch (error) {
    console.error('Error fetching article:', error);
    throw error;
  }
};

export const deleteArticle = async (articleId: string, userId: string): Promise<void> => {
  try {
    const articleRef = doc(articlesCollection, articleId);
    const articleDoc = await getDoc(articleRef);
    
    if (!articleDoc.exists()) {
      throw new Error('Article not found');
    }
    
    const article = articleDoc.data() as Article;
    
    // Check if user is authorized to delete the article
    if (article.userId !== userId) {
      // Check if user is admin
      const userRef = doc(db, 'users', userId);
      const userDoc = await getDoc(userRef);
      const isAdmin = userDoc.exists() && userDoc.data()?.role === 'admin';
      
      if (!isAdmin) {
        throw new Error('Unauthorized to delete this article');
      }
    }
    
    const batch = writeBatch(db);
    
    // Delete all comments for this article (including all levels of replies)
    const commentsQuery = query(collection(db, 'articles', articleId, 'comments'));
    const commentsSnapshot = await getDocs(commentsQuery);
    commentsSnapshot.docs.forEach(commentDoc => {
      batch.delete(commentDoc.ref);
    });
    
    // Delete all reactions for this article
    const reactionsQuery = query(collection(db, 'articles', articleId, 'reactions'));
    const reactionsSnapshot = await getDocs(reactionsQuery);
    reactionsSnapshot.docs.forEach(reactionDoc => {
      batch.delete(reactionDoc.ref);
    });
    
    // Delete the article document itself
    batch.delete(articleRef);
    
    await batch.commit();
  } catch (error) {
    console.error('Error deleting article:', error);
    throw error;
  }
};

export const updateArticleReactions = async (
  articleId: string,
  reactionType: keyof Article['reactions'],
  userId: string,
  action: 'add' | 'remove'
): Promise<void> => {
  try {
    if (action === 'add') {
      await addArticleReaction(articleId, userId, reactionType);
    } else {
      await removeArticleReaction(articleId, userId, reactionType);
    }
  } catch (error) {
    console.error('Error updating article reactions:', error);
    throw error;
  }
};

export const incrementArticleCommentCount = async (articleId: string): Promise<void> => {
  try {
    const articleRef = doc(articlesCollection, articleId);
    await updateDoc(articleRef, {
      commentCount: increment(1),
      lastUpdated: serverTimestamp()
    });
  } catch (error) {
    console.error('Error incrementing article comment count:', error);
    throw error;
  }
};

export const decrementArticleCommentCount = async (articleId: string): Promise<void> => {
  try {
    const articleRef = doc(articlesCollection, articleId);
    await updateDoc(articleRef, {
      commentCount: increment(-1),
      lastUpdated: serverTimestamp()
    });
  } catch (error) {
    console.error('Error decrementing article comment count:', error);
    throw error;
  }
}; 